import React, { useState, useEffect } from 'react';
import { Card, CardBody, Label, Input, Row, Col, Badge, Button } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import {
  RegimeTributario,
  REGIME_BASE_PRICES,
  VolumeFactors,
  calculateMultiRegimePricing,
  formatCurrency,
  MultiRegimePricingBreakdown,
  CompanyCountByRegime,
  getTierInfo,
  NOTAS_FISCAIS_TIERS,
  ITENS_POR_NOTA_TIERS,
  FUNCIONARIOS_TIERS,
} from '../../models/PricingModel';

interface PricingCalculatorProps {
  onPricingChange?: (breakdown: MultiRegimePricingBreakdown) => void;
  initialCompanyCounts?: CompanyCountByRegime;
  initialVolume?: VolumeFactors;
}

const PricingCalculator: React.FC<PricingCalculatorProps> = ({
  onPricingChange,
  initialCompanyCounts = {
    MEI: 0,
    SimplesNacional: 1,
    LucroPresumido: 0,
    LucroReal: 0,
  },
  initialVolume = {
    notasFiscaisMensal: 0,
    itensMediaPorNota: 0,
    funcionariosAtivos: 0,
  },
}) => {
  const [companyCounts, setCompanyCounts] = useState<CompanyCountByRegime>(initialCompanyCounts);
  const [volumeFactors, setVolumeFactors] = useState<VolumeFactors>(initialVolume);
  const [pricing, setPricing] = useState<MultiRegimePricingBreakdown | null>(null);

  // Recalculate pricing when company counts or volume changes
  useEffect(() => {
    const newPricing = calculateMultiRegimePricing(companyCounts, volumeFactors);
    setPricing(newPricing);
    onPricingChange?.(newPricing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCounts, volumeFactors]);

  const handleCompanyCountChange = (regime: RegimeTributario, count: number) => {
    setCompanyCounts((prev) => ({
      ...prev,
      [regime]: Math.max(0, count), // Ensure non-negative
    }));
  };

  const handleVolumeChange = (field: keyof VolumeFactors, value: number) => {
    setVolumeFactors((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Format number input to remove leading zeros
  const formatNumberInput = (value: number): string => {
    return value.toString();
  };

  // Parse number input and handle empty/invalid values
  const parseNumberInput = (value: string): number => {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : Math.max(0, parsed);
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'very_low':
        return 'success';
      case 'low':
        return 'info';
      case 'medium':
        return 'warning';
      case 'high':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="pricing-calculator">
      <Card>
        <CardBody>
          <h4 className="card-title mb-4">
            <i className="ri-calculator-line me-2"></i>
            Calculadora de Preços
          </h4>

          {/* Step 1: Select Company Counts per Regime */}
          <div className="mb-4">
            <h5 className="mb-3">
              <span className="badge bg-primary-subtle text-primary rounded-circle p-2 me-2">1</span>
              Quantas empresas você atende por regime?
            </h5>
            <p className="text-muted small mb-3">
              Informe o número de empresas que você gerencia em cada regime tributário
            </p>
            <Row>
              {REGIME_BASE_PRICES.map((regime) => {
                const count = companyCounts[regime.regime];
                return (
                  <Col lg={6} key={regime.regime} className="d-flex">
                    <Card className={`mb-3 w-100 ${count > 0 ? 'border-primary' : ''}`}>
                      <CardBody className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <Label className="form-label fw-bold mb-1">
                              {regime.displayName}
                            </Label>
                            <Badge color={getComplexityColor(regime.complexityLevel)} className="ms-2">
                              {formatCurrency(regime.basePrice)}/empresa/mês
                            </Badge>
                          </div>
                        </div>
                        <p className="text-muted small mb-2">{regime.description}</p>
                        <div className="small mb-3 flex-grow-1">
                          <strong>Obrigações:</strong>
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {regime.obligations.map((obligation) => (
                              <Badge key={obligation} color="light" className="text-dark">
                                {obligation}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-auto">
                          <Button
                            color="secondary"
                            size="sm"
                            outline
                            onClick={() => handleCompanyCountChange(regime.regime, count - 1)}
                            disabled={count === 0}
                          >
                            <i className="ri-subtract-line"></i>
                          </Button>
                          <Input
                            type="text"
                            value={formatNumberInput(count)}
                            onChange={(e) =>
                              handleCompanyCountChange(regime.regime, parseNumberInput(e.target.value))
                            }
                            onKeyPress={(e) => {
                              // Only allow numbers
                              if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            className="text-center"
                            style={{ maxWidth: '80px' }}
                          />
                          <Button
                            color="secondary"
                            size="sm"
                            outline
                            onClick={() => handleCompanyCountChange(regime.regime, count + 1)}
                          >
                            <i className="ri-add-line"></i>
                          </Button>
                          <span className="ms-2 text-muted small">
                            {count === 0
                              ? 'Nenhuma empresa'
                              : count === 1
                              ? '1 empresa'
                              : `${count} empresas`}
                          </span>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Step 2: Volume Factors */}
          <div className="mb-4">
            <h5 className="mb-3">
              <span className="badge bg-primary-subtle text-primary rounded-circle p-2 me-2">2</span>
              Informe seu movimento médio mensal
            </h5>

            {/* Notas Fiscais */}
            <div className="mb-4">
              <Label className="form-label fw-semibold">
                <FeatherIcon icon="file-text" size={16} className="me-2" />
                Quantas notas fiscais você emite por mês?
              </Label>
              <Input
                type="range"
                min="0"
                max="20000"
                step="100"
                value={volumeFactors.notasFiscaisMensal}
                onChange={(e) => handleVolumeChange('notasFiscaisMensal', parseInt(e.target.value))}
                className="form-range"
              />
              <div className="d-flex justify-content-between align-items-center mt-2">
                <div className="d-flex flex-column">
                  <span className="text-primary fw-bold">
                    {volumeFactors.notasFiscaisMensal >= 1000
                      ? `${(volumeFactors.notasFiscaisMensal / 1000).toFixed(1)}k notas`
                      : `${volumeFactors.notasFiscaisMensal} notas`}
                  </span>
                  <small className="text-muted">
                    Limite: 20 mil notas/mês
                  </small>
                </div>
                <Badge color="secondary">
                  {getTierInfo(volumeFactors.notasFiscaisMensal, NOTAS_FISCAIS_TIERS)?.label}
                </Badge>
              </div>
              <div className="mt-2">
                <Input
                  type="text"
                  value={formatNumberInput(volumeFactors.notasFiscaisMensal)}
                  onChange={(e) => {
                    const value = parseNumberInput(e.target.value);
                    handleVolumeChange('notasFiscaisMensal', Math.min(20000, value));
                  }}
                  onKeyPress={(e) => {
                    // Only allow numbers
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  className="form-control-sm"
                  placeholder="Digite o número exato"
                />
              </div>
            </div>

            {/* Items per Invoice */}
            <div className="mb-4">
              <Label className="form-label fw-semibold">
                <FeatherIcon icon="package" size={16} className="me-2" />
                Quantos itens em média por nota?
              </Label>
              <Input
                type="range"
                min="0"
                max="990"
                step="1"
                value={volumeFactors.itensMediaPorNota}
                onChange={(e) => handleVolumeChange('itensMediaPorNota', parseInt(e.target.value))}
                className="form-range"
              />
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="text-primary fw-bold">{volumeFactors.itensMediaPorNota} itens</span>
                <Badge color="secondary">
                  {getTierInfo(volumeFactors.itensMediaPorNota, ITENS_POR_NOTA_TIERS)?.label}
                </Badge>
              </div>
            </div>

            {/* Employees */}
            <div className="mb-4">
              <Label className="form-label fw-semibold">
                <FeatherIcon icon="users" size={16} className="me-2" />
                Quantos funcionários ativos?
              </Label>
              <Input
                type="range"
                min="0"
                max="100"
                step="1"
                value={volumeFactors.funcionariosAtivos}
                onChange={(e) => handleVolumeChange('funcionariosAtivos', parseInt(e.target.value))}
                className="form-range"
              />
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="text-primary fw-bold">{volumeFactors.funcionariosAtivos} funcionários</span>
                <Badge color="secondary">
                  {getTierInfo(volumeFactors.funcionariosAtivos, FUNCIONARIOS_TIERS)?.label}
                </Badge>
              </div>
            </div>
          </div>

          {/* Step 3: Pricing Summary */}
          {pricing && pricing.totalCompanies > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">
                <span className="badge bg-primary-subtle text-primary rounded-circle p-2 me-2">3</span>
                Resumo do Custo Mensal
              </h5>
              <Card className="bg-light">
                <CardBody>
                  {/* Company Summary by Regime */}
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">
                      <FeatherIcon icon="bar-chart-2" size={16} className="me-2" />
                      Empresas por Regime
                    </h6>
                    {pricing.regimeDetails.map((detail) => (
                      <div key={detail.regime} className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <span className="fw-semibold">
                            {REGIME_BASE_PRICES.find((r) => r.regime === detail.regime)?.displayName}
                          </span>
                          <small className="text-muted ms-2">
                            ({detail.companyCount} {detail.companyCount === 1 ? 'empresa' : 'empresas'} × {formatCurrency(detail.basePrice)})
                          </small>
                        </div>
                        <span className="fw-bold">{formatCurrency(detail.totalBasePrice)}</span>
                      </div>
                    ))}
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">Subtotal Regimes ({pricing.totalCompanies} {pricing.totalCompanies === 1 ? 'empresa' : 'empresas'}):</span>
                      <span className="fw-bold text-primary">{formatCurrency(pricing.totalRegimeBaseCost)}</span>
                    </div>
                  </div>

                  {/* Volume-Based Costs */}
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">
                      <FeatherIcon icon="trending-up" size={16} className="me-2" />
                      Custos por Volume
                    </h6>
                    {pricing.notasFiscaisAdditional > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Volume de Notas Fiscais:</small>
                        <span>+{formatCurrency(pricing.notasFiscaisAdditional)}</span>
                      </div>
                    )}

                    {pricing.itensPerNotaAdditional > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Itens por Nota:</small>
                        <span>+{formatCurrency(pricing.itensPerNotaAdditional)}</span>
                      </div>
                    )}

                    {pricing.funcionariosAdditional > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Funcionários (eSocial):</small>
                        <span>+{formatCurrency(pricing.funcionariosAdditional)}</span>
                      </div>
                    )}

                    {pricing.notasFiscaisAdditional === 0 &&
                     pricing.itensPerNotaAdditional === 0 &&
                     pricing.funcionariosAdditional === 0 && (
                      <div className="text-muted small">
                        <i className="ri-information-line"></i> Volume base incluído sem custo adicional
                      </div>
                    )}
                  </div>

                  <hr />

                  {/* Total */}
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Total Mensal:</h4>
                    <h3 className="mb-0 text-primary">{formatCurrency(pricing.totalMonthly)}</h3>
                  </div>

                  {/* What's Included */}
                  <div className="mt-3 p-2 bg-white rounded">
                    <small className="text-muted">
                      <i className="ri-information-line"></i> Inclui:
                    </small>
                    <ul className="small mb-0 mt-1">
                      <li>{pricing.totalCompanies} {pricing.totalCompanies === 1 ? 'empresa' : 'empresas'} ativas</li>
                      <li>{pricing.volumeFactors.notasFiscaisMensal} notas fiscais/mês (total)</li>
                      <li>Média de {pricing.volumeFactors.itensMediaPorNota} itens por nota</li>
                      <li>{pricing.volumeFactors.funcionariosAtivos} funcionários ativos (total)</li>
                      <li>Suporte técnico via email</li>
                      <li>Atualizações automáticas de layouts SPED</li>
                    </ul>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}

          {/* No companies selected warning */}
          {pricing && pricing.totalCompanies === 0 && (
            <div className="alert alert-warning">
              <i className="ri-alert-line me-2"></i>
              <strong>Atenção:</strong> Selecione pelo menos uma empresa para calcular o preço.
            </div>
          )}

          <div className="alert alert-info">
            <i className="ri-lightbulb-line me-2"></i>
            <strong>Dica:</strong> Você pode alterar seu plano a qualquer momento conforme seu volume
            cresce. Cobramos apenas pelo uso real!
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default PricingCalculator;
